import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useCallStore } from '../stores/callStore';

// Use environment variable for WebSocket URL, fallback to current location
const getWsUrl = () => {
  if (process.env.REACT_APP_WS_URL) {
    return process.env.REACT_APP_WS_URL;
  }
  return `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`;
};

const WS_URL = getWsUrl();
const RECONNECT_INTERVAL = 5000; // Increased from 3 seconds to 5 seconds
const MAX_RECONNECT_ATTEMPTS = 3; // Reduced from 5 to 3 attempts

export const useWebSocket = () => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const eventListenersRef = useRef({});
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const { token, user } = useAuthStore();

  // Conectar al WebSocket
  const connect = useCallback(() => {
    if (!token || !user) {
      return;
    }

    if (wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING) {
      return;
    }

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      setIsConnected(true);
      setReconnectAttempts(0);
      return;
    }

    try {
      // Construir URL con token de autenticación usando base relativo si no hay env
      const base = WS_URL || (window.location.protocol.replace('http', 'ws') + '//' + window.location.host);
      const wsUrl = `${base}/ws?token=${encodeURIComponent(token)}&user_id=${user.id}&role=${user.role}`;
      
      console.log('Connecting to WebSocket...');
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        setIsConnected(true);
        setReconnectAttempts(0);
        
        // Enviar mensaje de autenticación después de que el WebSocket esté conectado
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          const authMessage = {
            type: 'auth',
            data: {
              user_id: user.id,
              role: user.role,
              token: token
            },
            timestamp: new Date().toISOString(),
            user_id: user.id
          };
          
          try {
            wsRef.current.send(JSON.stringify(authMessage));
          } catch (error) {
            console.error('Error sending WebSocket auth message:', error);
          }
        }
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          const listeners = eventListenersRef.current[data.type] || [];
          listeners.forEach(listener => {
            try {
              listener(data);
            } catch (error) {
              console.error('Error in WebSocket listener:', error);
            }
          });
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      wsRef.current.onclose = (event) => {
        setIsConnected(false);
        
        if (event.code !== 1000 && reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
          reconnectTimeoutRef.current = setTimeout(() => {
            setReconnectAttempts(prev => prev + 1);
            connect();
          }, RECONNECT_INTERVAL);
        } else if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
          setReconnectAttempts(0);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setIsConnected(false);
      };

    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
    }
  }, [token, user, reconnectAttempts]);

  // Desconectar WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close(1000, 'Manual disconnect');
    }
    
    setIsConnected(false);
    setReconnectAttempts(0);
  }, []);

  // Enviar mensaje
  const send = useCallback((type, data = {}) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const message = {
        type,
        data,
        timestamp: new Date().toISOString(),
        user_id: user?.id
      };
      
      try {
        wsRef.current.send(JSON.stringify(message));
      } catch (error) {
        console.error('Error sending WebSocket message:', error);
      }
    } else {
      console.warn('WebSocket is not connected. Cannot send message:', { type, data });
    }
  }, [user]);

  // Registrar listener para eventos
  const on = useCallback((eventType, listener) => {
    if (!eventListenersRef.current[eventType]) {
      eventListenersRef.current[eventType] = [];
    }
    eventListenersRef.current[eventType].push(listener);
  }, []);

  // Desregistrar listener
  const off = useCallback((eventType, listener) => {
    if (eventListenersRef.current[eventType]) {
      eventListenersRef.current[eventType] = eventListenersRef.current[eventType].filter(
        l => l !== listener
      );
    }
  }, []);

  // Limpiar todos los listeners de un tipo de evento
  const removeAllListeners = useCallback((eventType) => {
    if (eventType) {
      eventListenersRef.current[eventType] = [];
    } else {
      eventListenersRef.current = {};
    }
  }, []);

  // Conectar automáticamente cuando hay token y usuario
  useEffect(() => {
    if (token && user) {
      connect();
    }

    // Don't disconnect on unmount - WebSocket should persist across navigation
    // Only disconnect when token/user changes (logout)
    return () => {
      // Only disconnect if there's no token (user logged out)
      if (!token) {
        disconnect();
      }
    };
  }, [token, user, connect, disconnect]);

  return {
    isConnected,
    send,
    on,
    off,
    removeAllListeners,
    connect,
    disconnect,
    reconnectAttempts
  };
};

// Hook especializado para actualizaciones de cola
export const useQueueUpdates = () => {
  const webSocket = useWebSocket();
  const [queueData, setQueueData] = useState([]);
  const [activeCallsData, setActiveCallsData] = useState([]);
  const { loadQueue, loadActiveCalls, updateQueue, updateActiveCalls } = useCallStore();
  const debounceTimers = useRef({});

  // Debounce para evitar múltiples llamadas API en ráfaga
  const debouncedCall = useCallback((key, fn, delay = 2000) => {
    if (debounceTimers.current[key]) {
      clearTimeout(debounceTimers.current[key]);
    }
    debounceTimers.current[key] = setTimeout(fn, delay);
  }, []);

  useEffect(() => {
    const handleQueueUpdate = (data) => {
      if (data.data?.queue) {
        setQueueData(data.data.queue);
        updateQueue(data.data.queue);
      } else {
        debouncedCall('queue', () => loadQueue(true));
      }
    };

    const handleActiveCallsUpdate = (data) => {
      if (data.data?.active_calls) {
        setActiveCallsData(data.data.active_calls);
        updateActiveCalls(data.data.active_calls);
      } else {
        debouncedCall('activeCalls', () => loadActiveCalls(true));
      }
    };

    const handleCallStatusUpdate = (data) => {
      debouncedCall('activeCalls', () => loadActiveCalls(true));
    };

    const handleCallEnded = (data) => {
      debouncedCall('activeCalls', () => loadActiveCalls(true));
    };

    // Registrar listeners
    webSocket.on('queue_update', handleQueueUpdate);
    webSocket.on('active_calls_update', handleActiveCallsUpdate);
    webSocket.on('call_status_update', handleCallStatusUpdate);
    webSocket.on('call_ended', handleCallEnded);

    if (webSocket.isConnected) {
      webSocket.send('get_queue_status');
      webSocket.send('get_active_calls');
    }

    const currentTimers = debounceTimers.current;

    return () => {
      webSocket.off('queue_update', handleQueueUpdate);
      webSocket.off('active_calls_update', handleActiveCallsUpdate);
      webSocket.off('call_status_update', handleCallStatusUpdate);
      webSocket.off('call_ended', handleCallEnded);
      Object.values(currentTimers).forEach(clearTimeout);
    };
  }, [webSocket, loadQueue, loadActiveCalls, updateQueue, updateActiveCalls, debouncedCall]);

  return {
    queueData,
    activeCallsData,
    isConnected: webSocket.isConnected,
    requestQueueUpdate: () => webSocket.send('get_queue_status'),
    requestActiveCallsUpdate: () => webSocket.send('get_active_calls')
  };
};

// Hook para notificaciones en tiempo real
export const useNotifications = () => {
  const webSocket = useWebSocket();
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    const handleNotification = (data) => {
      const notification = {
        id: Date.now(),
        ...data.data,
        timestamp: new Date()
      };
      
      setNotifications(prev => [notification, ...prev].slice(0, 10)); // Mantener solo las últimas 10
      
      // Auto-remover después de cierto tiempo si es una notificación temporal
      if (notification.auto_dismiss !== false) {
        setTimeout(() => {
          setNotifications(prev => prev.filter(n => n.id !== notification.id));
        }, notification.duration || 5000);
      }
    };

    webSocket.on('notification', handleNotification);
    webSocket.on('alert', handleNotification);
    webSocket.on('system_message', handleNotification);

    return () => {
      webSocket.off('notification', handleNotification);
      webSocket.off('alert', handleNotification);
      webSocket.off('system_message', handleNotification);
    };
  }, [webSocket]);

  const dismissNotification = useCallback((notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  return {
    notifications,
    dismissNotification,
    clearAllNotifications,
    isConnected: webSocket.isConnected
  };
};

// Hook para actualizaciones específicas de familiares (observadores)
export const useFamilyObserver = () => {
  const webSocket = useWebSocket();
  const [observerData, setObserverData] = useState({
    canObserve: false,
    activeCall: null,
    patientInfo: null
  });

  useEffect(() => {
    const handleObserverUpdate = (data) => {
      setObserverData(prev => ({
        ...prev,
        ...data.data
      }));
    };

    const handleCallEnd = (data) => {
      if (data.data?.call_id === observerData.activeCall?.id) {
        setObserverData(prev => ({
          ...prev,
          canObserve: false,
          activeCall: null
        }));
      }
    };

    webSocket.on('observer_access_update', handleObserverUpdate);
    webSocket.on('call_ended', handleCallEnd);

    // Solicitar estado inicial
    if (webSocket.isConnected) {
      webSocket.send('get_observer_status');
    }

    return () => {
      webSocket.off('observer_access_update', handleObserverUpdate);
      webSocket.off('call_ended', handleCallEnd);
    };
  }, [webSocket, observerData.activeCall?.id]);

  return {
    ...observerData,
    isConnected: webSocket.isConnected,
    requestUpdate: () => webSocket.send('get_observer_status')
  };
};

// Hook para manejar redirecciones automáticas de llamadas
export const useCallRedirection = () => {
  const webSocket = useWebSocket();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    const handleCallStarted = (data) => {
      if (user?.role === 'patient') {
        const callId = data.call_id;
        if (callId) {
          if (hasRedirectedRef.current) {
            return;
          }
          hasRedirectedRef.current = true;
          setTimeout(() => {
            navigate(`/video-call/${callId}`);
          }, 1200);
        }
      }
    };

    const handleDirectMessage = (data) => {
      if (data.data && data.data.type === 'call_started') {
        handleCallStarted(data.data);
      }
    };

    webSocket.on('call_started', handleCallStarted);
    webSocket.on('direct', handleDirectMessage);

    return () => {
      webSocket.off('call_started', handleCallStarted);
      webSocket.off('direct', handleDirectMessage);
      hasRedirectedRef.current = false;
    };
  }, [webSocket, navigate, user]);

  return {
    isConnected: webSocket.isConnected
  };
};